import { FormControl, FormControlProps, FormErrorMessage } from '@chakra-ui/react';
import { OnChangeValue } from 'chakra-react-select';
import { Controller, ControllerProps, FieldError, FieldValues, Merge } from 'react-hook-form';
import { UiCreatableSelect, UiCreatableSelectProps } from '../UiCreatableSelect/UiCreatableSelect';

export type CreatableSelectFormOption = {
  label: string;
  value?: string | number;
};

export type CreatableSelectFormOptionOnlyLabel = Omit<CreatableSelectFormOption, 'value'>;

type Props<TFieldValues extends FieldValues, TSelectOption, TIsMulti extends boolean> = {
  className?: string;
  fieldError: Merge<FieldError, FieldError | undefined[]> | undefined;
  controlProps: Omit<ControllerProps<TFieldValues>, 'render'>;
  selectProps: UiCreatableSelectProps<TSelectOption, TIsMulti>;
  onChange?: (option: TSelectOption) => any;
  onChangeMultiple?: (option: TSelectOption[]) => any;
  dataTestId?: string;
  formControlProps?: FormControlProps;
};

export default function CreatableSelectForm<TFieldValues extends FieldValues, TSelectOption, TIsMulti extends boolean>({
  fieldError,
  className,
  controlProps,
  selectProps,
  onChange,
  onChangeMultiple,
  dataTestId,
  formControlProps
}: Props<TFieldValues, TSelectOption, TIsMulti>) {
  function onChangeSelect(values: OnChangeValue<TSelectOption, TIsMulti>, onChangeRender: (...event: any[]) => void) {
    if (values && 'length' in (values as object)) {
      if (!onChangeMultiple) {
        throw new Error('onChangeMultiple undefined');
      }

      onChangeRender(onChangeMultiple(values as TSelectOption[]));
      return;
    }

    if (!onChange) {
      throw new Error('onChange undefined');
    }

    onChangeRender(onChange(values as TSelectOption));
  }

  return (
    <FormControl isInvalid={!!fieldError} className={className} {...formControlProps}>
      <Controller
        {...controlProps}
        render={({ field: { onChange: onChangeRender, ref } }) => (
          <UiCreatableSelect
            {...selectProps}
            id={controlProps.name}
            instanceId={controlProps.name}
            ref={ref}
            data-testid={dataTestId}
            onChange={(values) => onChangeSelect(values, onChangeRender)}
            chakraStyles={{
              menu: (provided) => ({ ...provided, zIndex: 3 }),
              multiValue: (provided) => ({ ...provided, backgroundColor: 'chakraGray.200', color: 'gray.900' })
            }}
          />
        )}
      />

      {fieldError && <FormErrorMessage>{fieldError?.message}</FormErrorMessage>}
    </FormControl>
  );
}
